import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { Currency } from "../../../i18n/Currency";
import { ApportionedAmounts } from "../../../shared/apportionmentTypes";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";

export interface ApportionmentSummaryProps {
  settlementAmount: number;
  amounts: ApportionedAmounts;
  currency: Currency;
}

/**
 * How much of the settlement amount is paid towards something and how much of
 * it is left over, over a bar that fills as the amount is apportioned.
 */
const ApportionmentSummary = ({
  settlementAmount,
  amounts,
  currency,
}: ApportionmentSummaryProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.offerDialog.apportionment",
  });

  const percent =
    settlementAmount > 0
      ? Math.min(100, Math.max(0, (amounts.total / settlementAmount) * 100))
      : 0;
  const isComplete = percent >= 100;

  const figures = [
    { label: t("settlementAmount"), value: settlementAmount },
    { label: t("apportioned"), value: amounts.total },
    {
      label: t("remaining"),
      value: amounts.remaining,
      // what is left over is paid towards nothing
      color:
        amounts.remaining > 0 ? theme.palette.data.negative.dark : undefined,
    },
  ];

  return (
    <Box sx={{ pb: 1.75 }}>
      {/* a figure that finds no room beside the others goes under them */}
      <Box
        sx={{ display: "flex", flexWrap: "wrap", columnGap: 2.5, rowGap: 0.75 }}
      >
        {figures.map(({ label, value, color }) => (
          <Box key={label} sx={{ flex: "1 1 auto" }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="subtitle1"
              component="div"
              sx={{
                fontWeight: 700,
                lineHeight: 1.3,
                letterSpacing: "-.01em",
                color,
                ...TABULAR_NUMERALS,
              }}
            >
              {currency.format(value)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          mt: 1,
          height: 6,
          borderRadius: 0.75,
          bgcolor: "action.disabledBackground",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${percent}%`,
            borderRadius: 0.75,
            bgcolor: isComplete
              ? theme.palette.data.positive.main
              : "primary.main",
            transition: "width 0.3s ease",
          }}
        />
      </Box>
    </Box>
  );
};

export default ApportionmentSummary;
